import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  Button,
  Dropdown,
  InlineLoading,
  OverflowMenu,
  OverflowMenuItem,
} from '@carbon/react';
import {
  ArrowsHorizontal,
  BlandAltmanPlot,
  CenterCircle,
  CharacterWholeNumber,
  Cut,
  DataVis_2 as DataVis2,
  Mountain,
  Settings,
  TextFill,
  TextFont,
  ZoomReset,
  type CarbonIconType,
} from '@carbon/react/icons';
import classNames from 'classnames';
import { useMediaQuery } from 'lib/hooks/useMediaQuery';
import {
  cropToGridExtentsAtom,
  depthReferenceAtom,
  distanceUnitAtom,
  faultResultsRequestedAtom,
  focusedWellUwiAtom,
  openSettingsPanelAtom,
  resetChartViewAtom,
  sectionDatumGridIdAtom,
  sectionFlippedAtom,
  sectionModeAtom,
  settingsPanelOpenAtom,
  showFaultResultsAtom,
  showGroundSurfaceAtom,
  showIntervalFillsAtom,
  showLabelsAtom,
  showLocationIndicatorAtom,
  showWellResultsAtom,
  toggleResultsAtom,
  wellResultsRequestedAtom,
} from './atoms';
import styles from './cross-section-header-controls.module.scss';

/** The two true depth references. Flattening is a third value, not a third option. */
const DEPTH_OPTIONS = [
  { id: 'tvdss' as const, label: 'TVDSS' },
  { id: 'tvd' as const, label: 'TVD' },
];

const FLATTENED_OPTION = { id: 'datum' as const, label: 'Flattened' };

/**
 * Below this width the drawer header cannot hold the title, the controls and Clear Cross
 * Section at once. Measured against the row's natural width with the settings rail open
 * — the narrowest the header ever gets — plus room to spare.
 */
const COLLAPSE_QUERY = '(max-width: 1319px)';

/**
 * One header control, described once and rendered either way. The label is its whole
 * meaning — the icon's tooltip when there is room, the menu item's text when there is
 * not — so the two never drift apart.
 */
interface HeaderControl {
  key: string;
  icon: CarbonIconType;
  label: string;
  /** Lit inline, and left unmarked in the menu — the label already says which way. */
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

export interface CrossSectionHeaderControlsProps {
  /** True while the cut is resolving, which disables what has nothing to act on. */
  hasSection: boolean;
  /** True while a calculated dataset is being worked out. */
  calculating?: boolean;
}

/**
 * The drawer header's controls, grouped by what they act on.
 *
 * Everything that changes what the section **shows** reads first, in the order it
 * narrows: the calculated results, then the ground, then the crop, then the fills and
 * the names, then the units and the depth reference. Everything that acts on the
 * **view** rather than the section sits apart at the far end, with the persistent
 * switches before the momentary actions.
 *
 * Every stateful control lights when it is active, so the header states the section's
 * display configuration without being interrogated. Nothing here re-cuts the section:
 * showing a calculated result, relabelling an axis, filling intervals and flipping the
 * read direction are all operations on a section already rendered.
 *
 * Narrow enough and the same controls fold into a single overflow menu, where each one
 * is its own tooltip text as a menu item. The settings button stays out of the menu: a
 * blank Well to Well section is built from that panel, so it is the way in rather than
 * one more display switch.
 *
 * @param {CrossSectionHeaderControlsProps} props - Component props.
 * @return {JSX.Element} The header controls.
 */
export const CrossSectionHeaderControls = ({
  hasSection,
  calculating = false,
}: CrossSectionHeaderControlsProps): JSX.Element => {
  const mode = useAtomValue(sectionModeAtom);
  const datumGridId = useAtomValue(sectionDatumGridIdAtom);
  const [depthReference, setDepthReference] = useAtom(depthReferenceAtom);
  const [distanceUnit, setDistanceUnit] = useAtom(distanceUnitAtom);
  const [showGround, setShowGround] = useAtom(showGroundSurfaceAtom);
  const [crop, setCrop] = useAtom(cropToGridExtentsAtom);
  const [showFills, setShowFills] = useAtom(showIntervalFillsAtom);
  const [showLabels, setShowLabels] = useAtom(showLabelsAtom);
  const [showIndicator, setShowIndicator] = useAtom(showLocationIndicatorAtom);
  const [flipped, setFlipped] = useAtom(sectionFlippedAtom);
  const showWells = useAtomValue(showWellResultsAtom);
  const showFaults = useAtomValue(showFaultResultsAtom);
  const wellsRequested = useAtomValue(wellResultsRequestedAtom);
  const faultsRequested = useAtomValue(faultResultsRequestedAtom);
  const toggleResults = useSetAtom(toggleResultsAtom);
  const settingsOpen = useAtomValue(settingsPanelOpenAtom);
  const setSettingsOpen = useSetAtom(settingsPanelOpenAtom);
  const openSettings = useSetAtom(openSettingsPanelAtom);
  const resetView = useSetAtom(resetChartViewAtom);
  const setFocusedWell = useSetAtom(focusedWellUwiAtom);
  const collapsed = useMediaQuery(COLLAPSE_QUERY);

  const flattened = datumGridId !== null;
  const wellToWell = mode === 'well_to_well';
  // In Well to Well the ground is what the wellbores hang from, so it survives
  // flattening and the switch stays forced on.
  const groundForced = wellToWell;
  const groundShown = groundForced || (showGround && !flattened);

  const lit = (active: boolean) =>
    classNames(styles.control, { [styles.on]: active });

  // What the plot shows. The first ask is a calculation; every press after only decides
  // whether the answer is drawn. Wells here mean crossings of OTHER wells — never the
  // wells defining a Well to Well section, which are the section rather than context.
  const display: HeaderControl[] = [
    {
      key: 'wells',
      icon: BlandAltmanPlot,
      label: !wellsRequested
        ? 'Calculate well intersections'
        : showWells
          ? 'Hide well intersections'
          : 'Show well intersections',
      active: wellsRequested && showWells,
      disabled: !hasSection,
      onClick: () => toggleResults('wells'),
    },
    {
      key: 'faults',
      icon: DataVis2,
      label: !faultsRequested
        ? 'Calculate fault intersections'
        : showFaults
          ? 'Hide fault intersections'
          : 'Show fault intersections',
      active: faultsRequested && showFaults,
      disabled: !hasSection,
      onClick: () => toggleResults('faults'),
    },
    // Ground elevation along the section. Sampled for every cut regardless, because
    // TVD is measured down from it — this only decides whether the line is drawn.
    {
      key: 'ground',
      icon: Mountain,
      label: groundForced
        ? 'Surface terrain always shown in Well to Well'
        : flattened
          ? 'Surface terrain unavailable while flattened'
          : showGround
            ? 'Hide surface terrain'
            : 'Show surface terrain',
      active: groundShown,
      disabled: !hasSection || flattened || groundForced,
      onClick: () => setShowGround(!showGround),
    },
    // Trims the axis to where grid data exists. The line is sampled whole either way
    // and simply goes null outside the grid, so this only decides how much of it the
    // axis shows.
    {
      key: 'crop',
      icon: Cut,
      label: wellToWell
        ? 'Cropping unavailable in Well to Well'
        : crop
          ? 'Span the whole drawn line'
          : 'Crop to grid extents',
      active: crop && !wellToWell,
      disabled: !hasSection || wellToWell,
      onClick: () => setCrop(!crop),
    },
    {
      key: 'fills',
      icon: TextFill,
      label: showFills ? 'Hide formation fills' : 'Show formation fills',
      active: showFills,
      disabled: !hasSection,
      onClick: () => setShowFills(!showFills),
    },
    // Names on the plot: interval names along their horizons, and the well name and
    // UWI above each wellbore. A well keeps its position number either way — that is
    // how it is cross-referenced with the map and the wells list, not labelling.
    {
      key: 'labels',
      icon: TextFont,
      label: showLabels ? 'Hide labels' : 'Show labels',
      active: showLabels,
      disabled: !hasSection,
      onClick: () => setShowLabels(!showLabels),
    },
    // Feet or miles. A basin-scale section runs to several hundred thousand feet,
    // where every tick is a six-digit number; miles is the unit those distances get
    // talked about in. Relabels in place, so the zoom survives.
    {
      key: 'distance-unit',
      icon: CharacterWholeNumber,
      label:
        distanceUnit === 'mi'
          ? 'Show distance in feet'
          : 'Show distance in miles',
      active: distanceUnit === 'mi',
      disabled: !hasSection,
      onClick: () => setDistanceUnit(distanceUnit === 'ft' ? 'mi' : 'ft'),
    },
  ];

  // What is done to the plot rather than to the section: the indicator links the two
  // views, flip and reset restage this one.
  const actions: HeaderControl[] = [
    {
      key: 'indicator',
      icon: CenterCircle,
      label: showIndicator
        ? 'Hide location indicator'
        : 'Show location indicator',
      active: showIndicator,
      disabled: !hasSection,
      onClick: () => setShowIndicator(!showIndicator),
    },
    // Flip is stateful — the section either is flipped or it is not — so it lights
    // like the display toggles. Reset is momentary, which is why it follows.
    {
      key: 'flip',
      icon: ArrowsHorizontal,
      label: flipped ? 'Flipped: A on the right' : 'Flip section direction',
      active: flipped,
      disabled: !hasSection,
      onClick: () => setFlipped(!flipped),
    },
    {
      key: 'reset',
      icon: ZoomReset,
      label: 'Reset chart view',
      disabled: !hasSection,
      onClick: () => {
        // Reset restores the view the section opened at, and it opened with nothing
        // picked out.
        setFocusedWell(null);
        resetView();
      },
    },
  ];

  const iconButton = (control: HeaderControl) => (
    <Button
      key={control.key}
      kind="ghost"
      size="sm"
      hasIconOnly
      renderIcon={control.icon}
      iconDescription={control.label}
      tooltipPosition="bottom"
      className={lit(Boolean(control.active))}
      disabled={control.disabled}
      onClick={control.onClick}
    />
  );

  const menuItem = (control: HeaderControl) => (
    <OverflowMenuItem
      key={control.key}
      itemText={control.label}
      disabled={control.disabled}
      onClick={control.onClick}
    />
  );

  // Named rather than iconised: no glyph reads as "true vertical depth versus subsea",
  // and an icon toggle would leave "on" ambiguous. Hung on a datum it reads as a third
  // value and locks, which is what flattening is from the axis's point of view.
  const depthDropdown = (
    <Dropdown
      id="cross-section-depth-reference"
      type="inline"
      size="sm"
      className={styles.depth}
      titleText="Depth"
      label="Depth"
      hideLabel
      items={DEPTH_OPTIONS}
      itemToString={(item) => item?.label ?? ''}
      selectedItem={
        flattened
          ? FLATTENED_OPTION
          : (DEPTH_OPTIONS.find((option) => option.id === depthReference) ??
            DEPTH_OPTIONS[0])
      }
      disabled={!hasSection || flattened}
      onChange={({ selectedItem }) => {
        if (selectedItem && selectedItem.id !== 'datum') {
          setDepthReference(selectedItem.id);
        }
      }}
    />
  );

  // In the menu the reference is a toggle like everything else around it: there are
  // only two of them, so naming the one not in force says both which is and what the
  // press would do. Flattened it says so and does nothing, the same as the dropdown.
  const depthItem: HeaderControl = {
    key: 'depth',
    icon: CharacterWholeNumber,
    label: flattened
      ? 'Depth: flattened on a datum'
      : depthReference === 'tvdss'
        ? 'Show depth as TVD'
        : 'Show depth as TVDSS',
    disabled: !hasSection || flattened,
    onClick: () =>
      setDepthReference(depthReference === 'tvdss' ? 'tvd' : 'tvdss'),
  };

  const settingsButton = (
    // Never disabled: a blank Well to Well section is built from this panel, so
    // locking it behind a section would lock out the way to start one.
    <Button
      kind="ghost"
      size="sm"
      hasIconOnly
      renderIcon={Settings}
      iconDescription={
        settingsOpen ? 'Close cross section settings' : 'Cross section settings'
      }
      tooltipPosition="bottom"
      className={lit(settingsOpen)}
      onClick={() => {
        if (settingsOpen) {
          setSettingsOpen(false);
          return;
        }
        openSettings();
      }}
    />
  );

  if (collapsed) {
    return (
      <>
        <div className={styles.display}>
          {calculating ? (
            <InlineLoading className={styles.calculating} description="" />
          ) : null}
          <OverflowMenu
            size="sm"
            flipped
            // Upwards, over the map: below the header is the plot and then the end of
            // the window, so opening downwards cut the last items off. The stylesheet
            // caps the height for a drawer dragged tall enough to crowd the menu.
            direction="top"
            aria-label="Cross section display options"
            iconDescription="Cross section display options"
            className={styles.control}
            menuOptionsClass={styles.menu}
          >
            {display.map(menuItem)}
            {menuItem(depthItem)}
            {actions.map(menuItem)}
          </OverflowMenu>
        </div>
        <div className={styles.actions}>{settingsButton}</div>
      </>
    );
  }

  return (
    <>
      {/* What the plot shows, next to its title: read as part of the section
          itself, and the controls reached for most. */}
      <div className={styles.display}>
        {calculating ? (
          <InlineLoading className={styles.calculating} description="" />
        ) : null}
        {display.map(iconButton)}
        {depthDropdown}
      </div>

      {/* What is done to the plot, kept at the far end. None of these change what the
          section shows, so they sit out of the path of the display switches. */}
      <div className={styles.actions}>
        {actions.map(iconButton)}
        {settingsButton}
      </div>
    </>
  );
};
